/**
 * f14.2: pure formatting for the reconciliation queue.
 *
 * Amounts arrive as 4-dp decimal STRINGS. They are shown to the cent by
 * shifting the decimal point two places and reusing the review's half-even
 * whole-number rounding on the digits -- never through a float.
 */

import { EMPTY, roundWhole, signOf } from '../review/reviewFormat';

const SYMBOLS: Readonly<Record<string, string>> = { USD: '$', EUR: '€', GBP: '£' };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function shiftTwo(amount: string): string | null {
  const match = /^\s*([+-]?)(\d*)(?:\.(\d*))?\s*$/.exec(amount);
  if (!match || (!match[2] && !match[3])) return null;
  const fraction = (match[3] ?? '').padEnd(2, '0');
  return `${match[1]}${match[2] || '0'}${fraction.slice(0, 2)}.${fraction.slice(2) || '0'}`;
}

function group(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** `-$150.00`, `+€50.00` (signed), `CHF 1,200.00`. Null renders "—". */
export function formatAmount(
  amount: string | null | undefined,
  currencyCode: string,
  options: { signed?: boolean } = {},
): string {
  if (amount == null) return EMPTY;
  const shifted = shiftTwo(amount);
  const rounded = shifted ? roundWhole(shifted) : null;
  if (!rounded) return EMPTY;
  const cents = rounded.digits.padStart(3, '0');
  const body = `${group(cents.slice(0, -2).replace(/^0+(?=\d)/, ''))}.${cents.slice(-2)}`;
  const symbol = SYMBOLS[currencyCode];
  const text = symbol ? `${symbol}${body}` : `${currencyCode} ${body}`;
  const zero = /^0+$/.test(rounded.digits);
  if (rounded.negative && !zero) return `-${text}`;
  if (options.signed && !zero) return `+${text}`;
  return text;
}

export function monthLabel(month: number, year?: number): string {
  const name = MONTHS[month - 1] ?? String(month);
  return year ? `${name} ${year}` : name;
}

/** The prototype's pill for each status: never a hex value. */
export function statusPill(code: string): string {
  switch (code) {
    case 'PENDING':
      return 'pill-warning';
    case 'ACCEPTED':
      return 'pill-success';
    case 'REJECTED':
      return 'pill-danger';
    default:
      return 'pill-neutral';
  }
}

export const STATUS_LABELS: Readonly<Record<string, string>> = {
  PENDING: 'Pending',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  SUPERSEDED: 'Superseded',
};

export const SOURCE_LABELS: Readonly<Record<string, string>> = {
  ACTUALS: 'Actuals',
  FORECAST_DRIFT: 'Kimble drift',
};

export const SUPERSEDE_LABELS: Readonly<Record<string, string>> = {
  NEWER_INGEST: 'a newer figure arrived',
  NO_LONGER_DIFFERENT: 'the difference disappeared',
  SUPERSEDED_BY_DECISION: 'another variance on this month was accepted',
};

/** Negative -> red, positive -> green (the review's own classes). */
export function varianceClass(amount: string): 'delta-down' | 'delta-up' | 'delta-flat' {
  const sign = signOf(amount);
  if (sign < 0) return 'delta-down';
  if (sign > 0) return 'delta-up';
  return 'delta-flat';
}

const READ_ONLY_STATES = new Set(['FROZEN', 'SUBMITTED']);

/** Decide controls render only for a PENDING row of an editable cycle the server says may be decided. */
export function showsActions(variance: { status: { code: string }; capabilities: { can_decide: boolean } },
  cycleStateCode: string): boolean {
  return variance.status.code === 'PENDING' && variance.capabilities.can_decide && !READ_ONLY_STATES.has(cycleStateCode);
}

export function isReadOnlyCycle(cycleStateCode: string): boolean {
  return READ_ONLY_STATES.has(cycleStateCode);
}
